import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import { Search, X, ChevronUp, ChevronDown, Lock } from "lucide-react";
import { usePermissions } from "../contexts/PermissionsContext";

const EstimatesPage = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending"); // pending, won, lost, all
  const [searchQuery, setSearchQuery] = useState(""); // Client name search
  const scrollContainerRef = useRef(null);
  
  // Modal states
  const [markWonModal, setMarkWonModal] = useState({ open: false, estimate: null });
  const [markLostModal, setMarkLostModal] = useState({ open: false, estimate: null, reason: "", notes: "" });
  const [followUpModal, setFollowUpModal] = useState({ open: false, estimate: null, note: "", nextDate: "" });

  // Smooth scroll functions
  const scrollUp = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ top: -300, behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ top: 300, behavior: 'smooth' });
    }
  };

  const fetchEstimates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/estimates");
      const data = (res.data || []).map(est => ({
        ...est,
        id: est.estimate_id,
        name: est.job_name || "Untitled Project",
        clientName: est.client_name || "Unknown Client",
        amount: est.grand_total || 0,
        status: est.pipeline_status || "pending",
        createdDate: est.created_at ? new Date(est.created_at) : new Date(),
        sentDate: est.sent_date ? new Date(est.sent_date) : new Date(est.created_at || Date.now()),
        wonDate: est.won_date ? new Date(est.won_date) : null,
        lostDate: est.lost_date ? new Date(est.lost_date) : null,
        lostReason: est.lost_reason || null,
        followUpNotes: est.follow_up_notes || [],
      }));
      setEstimates(data);
    } catch (error) {
      toast.error("Failed to load estimates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEstimates();
  }, [fetchEstimates]);

  // Calculate days waiting/to decision
  const getDaysWaiting = (estimate) => {
    if (estimate.status === "won" && estimate.wonDate) {
      return Math.floor((estimate.wonDate - estimate.sentDate) / (1000 * 60 * 60 * 24));
    }
    if (estimate.status === "lost" && estimate.lostDate) {
      return Math.floor((estimate.lostDate - estimate.sentDate) / (1000 * 60 * 60 * 24));
    }
    return Math.floor((new Date() - estimate.sentDate) / (1000 * 60 * 60 * 24));
  };

  // Filter estimates by status AND search query
  const filteredEstimates = estimates.filter(e => {
    // First filter by status
    const statusMatch = activeTab === "all" ? true : e.status === activeTab;
    
    // Then filter by search query (client name)
    const searchMatch = searchQuery.trim() === "" 
      ? true 
      : e.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    return statusMatch && searchMatch;
  });

  // Calculate stats (based on all estimates, not filtered)
  const pendingEstimates = estimates.filter(e => e.status === "pending");
  const wonEstimates = estimates.filter(e => e.status === "won");
  const lostEstimates = estimates.filter(e => e.status === "lost");
  
  const wonAndLost = [...wonEstimates, ...lostEstimates];
  const avgResponseTime = wonAndLost.length > 0 
    ? Math.round(wonAndLost.reduce((sum, e) => sum + getDaysWaiting(e), 0) / wonAndLost.length)
    : 0;
  
  const winRate = wonAndLost.length > 0 
    ? ((wonEstimates.length / wonAndLost.length) * 100).toFixed(1)
    : 0;
  
  const pendingValue = pendingEstimates.reduce((sum, e) => sum + e.amount, 0);
  
  const oldestPending = pendingEstimates.length > 0 
    ? pendingEstimates.reduce((old, curr) => getDaysWaiting(curr) > getDaysWaiting(old) ? curr : old)
    : null;

  // Handle Mark Won - Convert to Active Operations Project
  const handleMarkWon = async () => {
    const estimate = markWonModal.estimate;
    if (!estimate) return;
    
    try {
      // Use the new conversion API which creates project + milestones + payment schedule
      const response = await API.post(`/estimates/${estimate.id}/convert-to-project`);
      const { project } = response.data;
      
      toast.success("Project created with milestones and payment schedule!");
      setMarkWonModal({ open: false, estimate: null });
      
      // Navigate to the new project detail page
      if (project?.project_id) {
        navigate(`/projects/${project.project_id}`);
      } else {
        fetchEstimates();
      }
    } catch (error) {
      console.error("Conversion error:", error);
      toast.error(error.response?.data?.detail || "Failed to convert estimate to project");
    }
  };

  // Handle Mark Lost
  const handleMarkLost = async () => {
    const { estimate, reason, notes } = markLostModal;
    if (!estimate || !reason) {
      toast.error("Please select a reason");
      return;
    }
    
    try {
      await API.put(`/estimates/${estimate.id}`, {
        pipeline_status: "lost",
        lost_date: new Date().toISOString(),
        lost_reason: reason,
        lost_notes: notes,
      });
      
      toast.success("Estimate marked as lost");
      setMarkLostModal({ open: false, estimate: null, reason: "", notes: "" });
      fetchEstimates();
    } catch (error) {
      toast.error("Failed to mark as lost");
    }
  };

  // Handle Follow Up
  const handleFollowUp = async () => {
    const { estimate, note, nextDate } = followUpModal;
    if (!estimate || !note.trim()) {
      toast.error("Please enter a note");
      return;
    }
    
    try {
      const followUpEntry = {
        note: note.trim(),
        date: new Date().toISOString(),
        nextFollowUp: nextDate || null,
      };
      
      const existingNotes = estimate.followUpNotes || [];
      
      await API.put(`/estimates/${estimate.id}`, {
        follow_up_notes: [...existingNotes, followUpEntry],
        last_follow_up_date: new Date().toISOString(),
      });
      
      toast.success(`Follow-up logged for ${estimate.clientName}`);
      setFollowUpModal({ open: false, estimate: null, note: "", nextDate: "" });
      fetchEstimates();
    } catch (error) {
      toast.error("Failed to log follow-up");
    }
  };

  const getWaitingColor = (days) => {
    if (days <= 7) return "#94a3b8";
    if (days <= 14) return "#fcd34d";
    return "#ff4444";
  };

  const getWinRateColor = (rate) => {
    if (rate >= 50) return "#10b981";
    if (rate >= 30) return "#fcd34d";
    return "#ff4444";
  };

  const tabCounts = {
    pending: pendingEstimates.length,
    won: wonEstimates.length,
    lost: lostEstimates.length,
    all: estimates.length,
  };

  const sectionDescriptions = {
    pending: "Waiting for Client Response",
    won: "Successfully Converted Projects",
    lost: "Archived Estimates",
    all: "Complete Estimate History",
  };

  return (
    <div className="relative h-screen" style={{ backgroundColor: '#020617' }}>
      {/* Scroll Up Arrow - Top of scrollbar */}
      <button
        onClick={scrollUp}
        className="fixed right-0 top-16 z-50 w-5 h-8 flex items-center justify-center transition-all duration-200 hover:bg-emerald-500/20"
        style={{
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          borderLeft: '1px solid rgba(16, 185, 129, 0.3)',
          borderBottom: '1px solid rgba(16, 185, 129, 0.3)',
        }}
        data-testid="scroll-up-btn"
      >
        <ChevronUp className="h-4 w-4" style={{ color: '#10b981' }} />
      </button>
      
      {/* Scroll Down Arrow - Bottom of scrollbar */}
      <button
        onClick={scrollDown}
        className="fixed right-0 bottom-0 z-50 w-5 h-8 flex items-center justify-center transition-all duration-200 hover:bg-emerald-500/20"
        style={{
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          borderLeft: '1px solid rgba(16, 185, 129, 0.3)',
          borderTop: '1px solid rgba(16, 185, 129, 0.3)',
        }}
        data-testid="scroll-down-btn"
      >
        <ChevronDown className="h-4 w-4" style={{ color: '#10b981' }} />
      </button>

      {/* Main Scrollable Content */}
      <div 
        ref={scrollContainerRef}
        className="h-full overflow-y-auto space-y-8 p-6 pr-8"
        style={{ scrollBehavior: 'smooth' }}
      >
      
      {/* Section 1: Page Header */}
      <div 
        className="relative rounded-lg p-5"
        style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(0, 0, 0, 0.3) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderLeft: '4px solid #10b981',
        }}
      >
        <div 
          className="absolute top-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent 0%, #10b981 50%, transparent 100%)', opacity: 0.5 }}
        />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 
              className="font-rajdhani font-bold uppercase mb-1.5"
              style={{ 
                color: '#10b981', 
                fontSize: '28px', 
                letterSpacing: '2px',
                textShadow: '0 0 20px rgba(16, 185, 129, 0.3)',
              }}
            >
              ESTIMATE PIPELINE
            </h1>
            <p style={{ color: '#94a3b8', fontSize: '14px', letterSpacing: '0.5px' }}>
              Track your estimate outcomes and conversion rates
            </p>
          </div>
          
          {/* Client Search */}
          <div className="relative w-full md:w-80">
            <Search 
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" 
              style={{ color: '#64748b' }} 
            />
            <input
              type="text"
              placeholder="Search by client or project name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 rounded-md text-sm transition-all"
              style={{
                backgroundColor: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                color: '#e2e8f0',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#10b981';
                e.currentTarget.style.boxShadow = '0 0 12px rgba(16, 185, 129, 0.2)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid="client-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-700 transition-colors"
                style={{ color: '#64748b' }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Section 2: Status Filter Tabs */}
      <div 
        className="flex gap-2 p-2 rounded-lg"
        style={{
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(16, 185, 129, 0.15)',
        }}
      >
        {["pending", "won", "lost", "all"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="relative flex-1 py-3.5 px-6 rounded-md transition-all duration-200"
            style={{
              background: activeTab === tab 
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)'
                : 'transparent',
              border: `1px solid ${activeTab === tab ? 'rgba(16, 185, 129, 0.4)' : 'transparent'}`,
              color: activeTab === tab ? '#10b981' : '#94a3b8',
              boxShadow: activeTab === tab ? '0 0 20px rgba(16, 185, 129, 0.2)' : 'none',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab) {
                e.currentTarget.style.color = '#cbd5e1';
                e.currentTarget.style.background = 'rgba(16, 185, 129, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab) {
                e.currentTarget.style.color = '#94a3b8';
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'transparent';
              }
            }}
            data-testid={`tab-${tab}`}
          >
            {activeTab === tab && (
              <div 
                className="absolute top-0 left-0 right-0 h-0.5 rounded-t"
                style={{ background: 'linear-gradient(90deg, transparent 0%, #10b981 50%, transparent 100%)' }}
              />
            )}
            <span className="text-xs font-bold uppercase tracking-widest">
              {tab.toUpperCase()}
              <span className="ml-2" style={{ opacity: activeTab === tab ? 1 : 0.8, fontWeight: 800 }}>
                ({tabCounts[tab]})
              </span>
            </span>
          </button>
        ))}
      </div>

      {/* Section 3: Tactical Intelligence Stats */}
      <div 
        className="rounded-lg p-7"
        style={{
          background: 'rgba(30, 41, 59, 0.4)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
        }}
      >
        <h2 
          className="font-rajdhani font-bold uppercase mb-5"
          style={{ color: '#e2e8f0', fontSize: '16px', letterSpacing: '2px' }}
        >
          TACTICAL INTELLIGENCE
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Avg Response Time */}
          <div 
            className="relative rounded-md p-4 transition-all duration-200 hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(0, 0, 0, 0.3) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderLeft: '3px solid #10b981',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#10b981';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, #10b981 50%, transparent 100%)', opacity: 0.5 }} />
            <p className="font-rajdhani text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#64748b' }}>
              AVG RESPONSE TIME
            </p>
            <p className="font-mono text-2xl font-bold" style={{ color: '#10b981', textShadow: '0 0 10px rgba(16, 185, 129, 0.3)' }}>
              {avgResponseTime}
            </p>
            <p className="font-mono text-xs mt-1" style={{ color: '#94a3b8' }}>days to decision</p>
          </div>

          {/* Win Rate */}
          <div 
            className="relative rounded-md p-4 transition-all duration-200 hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(0, 0, 0, 0.3) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderLeft: '3px solid #10b981',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#10b981';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, #10b981 50%, transparent 100%)', opacity: 0.5 }} />
            <p className="font-rajdhani text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#64748b' }}>
              WIN RATE
            </p>
            <p 
              className="font-mono text-2xl font-bold"
              style={{ 
                color: getWinRateColor(parseFloat(winRate)),
                textShadow: `0 0 10px ${getWinRateColor(parseFloat(winRate))}40`,
                animation: parseFloat(winRate) < 30 && wonAndLost.length > 0 ? 'criticalPulse 2s ease-in-out infinite' : 'none',
              }}
            >
              {winRate}%
            </p>
            <p className="font-mono text-xs mt-1" style={{ color: '#94a3b8' }}>
              ({wonEstimates.length} won / {wonAndLost.length} total)
            </p>
          </div>

          {/* Pending Value */}
          <div 
            className="relative rounded-md p-4 transition-all duration-200 hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(0, 0, 0, 0.3) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderLeft: '3px solid #10b981',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#10b981';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, #10b981 50%, transparent 100%)', opacity: 0.5 }} />
            <p className="font-rajdhani text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#64748b' }}>
              PENDING VALUE
            </p>
            <p className="font-mono text-2xl font-bold" style={{ color: '#10b981', textShadow: '0 0 10px rgba(16, 185, 129, 0.3)' }}>
              ${Math.round(pendingValue).toLocaleString()}
            </p>
            <p className="font-mono text-xs mt-1" style={{ color: '#94a3b8' }}>
              {pendingEstimates.length} estimates active
            </p>
          </div>

          {/* Oldest Pending */}
          <div 
            className="relative rounded-md p-4 transition-all duration-200 hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(0, 0, 0, 0.3) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderLeft: '3px solid #10b981',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#10b981';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, #10b981 50%, transparent 100%)', opacity: 0.5 }} />
            <p className="font-rajdhani text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#64748b' }}>
              OLDEST PENDING
            </p>
            <p 
              className="font-mono text-2xl font-bold"
              style={{ 
                color: oldestPending ? getWaitingColor(getDaysWaiting(oldestPending)) : '#10b981',
                textShadow: oldestPending && getDaysWaiting(oldestPending) > 14 ? '0 0 10px rgba(255, 68, 68, 0.3)' : '0 0 10px rgba(16, 185, 129, 0.3)',
                animation: oldestPending && getDaysWaiting(oldestPending) > 14 ? 'criticalPulse 2s ease-in-out infinite' : 'none',
              }}
            >
              {oldestPending ? getDaysWaiting(oldestPending) : 0}
            </p>
            <p className="font-mono text-xs mt-1 truncate" style={{ color: '#94a3b8' }}>
              {oldestPending ? oldestPending.name : "No pending"}
            </p>
          </div>
        </div>
      </div>

      {/* Section 4: Section Header */}
      <div 
        className="rounded-md py-4 px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
        style={{
          background: 'rgba(16, 185, 129, 0.05)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderLeft: '3px solid #10b981',
        }}
      >
        <h3 
          className="font-rajdhani font-bold uppercase"
          style={{ color: '#10b981', fontSize: '16px', letterSpacing: '2px' }}
        >
          {activeTab.toUpperCase()} - {sectionDescriptions[activeTab]}
        </h3>
        {searchQuery && (
          <p className="text-xs" style={{ color: '#94a3b8' }}>
            Showing {filteredEstimates.length} results for "{searchQuery}"
          </p>
        )}
      </div>

      {/* Section 5: Estimate Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredEstimates.length === 0 ? (
        <div className="text-center py-20 px-5" style={{ color: '#64748b' }}>
          <h4 className="text-xl font-bold mb-3" style={{ color: '#94a3b8' }}>
            {searchQuery ? `No results for "${searchQuery}"` : (
              <>
                {activeTab === "pending" && "No pending estimates"}
                {activeTab === "won" && "No won estimates yet"}
                {activeTab === "lost" && "No lost estimates"}
                {activeTab === "all" && "No estimates found"}
              </>
            )}
          </h4>
          <p className="text-sm leading-relaxed mb-6">
            {searchQuery ? "Try a different search term or clear the search." : (
              <>
                {activeTab === "pending" && "All caught up! Create new estimates from Blueprint Takeoff."}
                {activeTab === "won" && "Mark estimates as won to track conversions!"}
                {activeTab === "lost" && "Keep up the perfect win rate!"}
                {activeTab === "all" && "Get started by creating your first estimate from Blueprint Takeoff."}
              </>
            )}
          </p>
          {searchQuery ? (
            <button
              onClick={() => setSearchQuery("")}
              className="px-6 py-3 rounded-md text-sm font-bold uppercase transition-all"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.4)',
                color: '#10b981',
              }}
            >
              Clear Search
            </button>
          ) : (
            <button
              onClick={() => navigate("/blueprint-takeoff")}
              className="px-6 py-3 rounded-md text-sm font-bold uppercase transition-all"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.4)',
                color: '#10b981',
              }}
            >
              Create New Estimate
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filteredEstimates.map((estimate) => {
            const daysWaiting = getDaysWaiting(estimate);
            const waitingColor = getWaitingColor(daysWaiting);
            
            return (
              <div
                key={estimate.id}
                className="relative rounded-lg p-6 transition-all duration-200 hover:-translate-y-1 cursor-pointer"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.03) 0%, rgba(15, 23, 42, 0.8) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  borderLeft: '3px solid rgba(16, 185, 129, 0.5)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.borderLeftColor = '#10b981';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)';
                  e.currentTarget.style.borderLeftColor = 'rgba(16, 185, 129, 0.5)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={() => navigate(`/estimating?estimate=${estimate.id}`)}
                data-testid={`estimate-card-${estimate.id}`}
              >
                <div 
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, #10b981 50%, transparent 100%)', opacity: 0.3 }}
                />
                
                {/* Project Name */}
                <h4 
                  className="font-rajdhani font-bold text-lg mb-2 truncate"
                  style={{ color: '#e2e8f0' }}
                >
                  {estimate.name}
                </h4>
                
                {/* Client Name */}
                <p 
                  className="font-rajdhani font-bold uppercase text-base mb-4 truncate"
                  style={{ color: '#10b981', letterSpacing: '0.5px' }}
                >
                  {estimate.clientName}
                </p>
                
                {/* Amount */}
                <p 
                  className="font-mono font-bold text-xl mb-3"
                  style={{ color: '#10b981' }}
                >
                  ${estimate.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                
                {/* Sent Date */}
                <p className="font-mono text-xs mb-1" style={{ color: '#94a3b8' }}>
                  Sent: {estimate.sentDate.toLocaleDateString()}
                </p>
                
                {/* Waiting Time / Status */}
                {estimate.status === "pending" && (
                  <p className="text-xs font-medium mb-5" style={{ color: waitingColor }}>
                    {daysWaiting} days waiting
                  </p>
                )}
                {estimate.status === "won" && (
                  <p className="text-xs font-medium mb-5" style={{ color: '#10b981' }}>
                    Won in {daysWaiting} days
                  </p>
                )}
                {estimate.status === "lost" && (
                  <p className="text-xs font-normal mb-5" style={{ color: '#94a3b8' }}>
                    Lost: {estimate.lostReason || "No reason specified"}
                  </p>
                )}
                
                {/* Action Buttons (only for pending) */}
                {estimate.status === "pending" && (
                  <div className="flex flex-col gap-2.5" onClick={(e) => e.stopPropagation()}>
                    {can('mark_estimate_won') && (
                    <button
                      onClick={() => setMarkWonModal({ open: true, estimate })}
                      className="w-full py-3 px-4 rounded-md text-xs font-bold uppercase tracking-wide transition-all duration-200"
                      style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)',
                        border: '1px solid rgba(16, 185, 129, 0.4)',
                        color: '#10b981',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(16, 185, 129, 0.15) 100%)';
                        e.currentTarget.style.borderColor = '#10b981';
                        e.currentTarget.style.boxShadow = '0 0 20px rgba(16, 185, 129, 0.4)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                      data-testid={`mark-won-${estimate.id}`}
                    >
                      Mark Won
                    </button>
                    )}
                    {can('mark_estimate_lost') && (
                    <button
                      onClick={() => setMarkLostModal({ open: true, estimate, reason: "", notes: "" })}
                      className="w-full py-3 px-4 rounded-md text-xs font-bold uppercase tracking-wide transition-all duration-200"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255, 68, 68, 0.15) 0%, rgba(255, 68, 68, 0.05) 100%)',
                        border: '1px solid rgba(255, 68, 68, 0.4)',
                        color: '#ff4444',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 68, 68, 0.25) 0%, rgba(255, 68, 68, 0.15) 100%)';
                        e.currentTarget.style.borderColor = '#ff4444';
                        e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 68, 68, 0.4)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 68, 68, 0.15) 0%, rgba(255, 68, 68, 0.05) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(255, 68, 68, 0.4)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                      data-testid={`mark-lost-${estimate.id}`}
                    >
                      Mark Lost
                    </button>
                    )}
                    <button
                      onClick={() => setFollowUpModal({ open: true, estimate, note: "", nextDate: "" })}
                      className="w-full py-3 px-4 rounded-md text-xs font-bold uppercase tracking-wide transition-all duration-200"
                      style={{
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%)',
                        border: '1px solid rgba(59, 130, 246, 0.4)',
                        color: '#3b82f6',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(59, 130, 246, 0.15) 100%)';
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.4)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                      data-testid={`follow-up-${estimate.id}`}
                    >
                      Follow Up
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mark Won Modal */}
      {markWonModal.open && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-5"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(4px)', zIndex: 9999 }}
          onClick={() => setMarkWonModal({ open: false, estimate: null })}
        >
          <div 
            className="relative w-full max-w-md rounded-lg p-8"
            style={{
              backgroundColor: '#0f172a',
              border: '2px solid rgba(16, 185, 129, 0.3)',
              borderLeft: '4px solid #10b981',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="absolute top-0 left-0 right-0 h-0.5"
              style={{ background: 'linear-gradient(90deg, transparent 0%, #10b981 50%, transparent 100%)' }}
            />
            <h3 
              className="font-rajdhani font-bold uppercase mb-4"
              style={{ color: '#10b981', fontSize: '22px', letterSpacing: '1px' }}
            >
              Convert to Project?
            </h3>
            <p className="text-sm mb-6 leading-relaxed" style={{ color: '#94a3b8' }}>
              This will create a new project in Active Operations for <strong style={{ color: '#e2e8f0' }}>{markWonModal.estimate?.name}</strong>.
            </p>
            <div className="p-4 rounded-md mb-6" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
              <p className="text-sm" style={{ color: '#94a3b8' }}>
                Client: <span style={{ color: '#e2e8f0' }}>{markWonModal.estimate?.clientName}</span>
              </p>
              <p className="text-sm mt-1" style={{ color: '#94a3b8' }}>
                Amount: <span className="font-mono font-bold" style={{ color: '#10b981' }}>
                  ${markWonModal.estimate?.amount?.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                </span>
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setMarkWonModal({ open: false, estimate: null })}
                className="px-6 py-3 rounded-md font-bold text-sm transition-all"
                style={{
                  backgroundColor: 'rgba(148, 163, 184, 0.1)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  color: '#94a3b8',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMarkWon}
                className="px-6 py-3 rounded-md font-bold text-sm transition-all"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: '1px solid #10b981',
                  color: '#ffffff',
                  boxShadow: '0 0 20px rgba(16, 185, 129, 0.3)',
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Lost Modal */}
      {markLostModal.open && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-5"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(4px)', zIndex: 9999 }}
          onClick={() => setMarkLostModal({ open: false, estimate: null, reason: "", notes: "" })}
        >
          <div 
            className="relative w-full max-w-md rounded-lg p-8"
            style={{
              backgroundColor: '#0f172a',
              border: '2px solid rgba(255, 68, 68, 0.3)',
              borderLeft: '4px solid #ff4444',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="absolute top-0 left-0 right-0 h-0.5"
              style={{ background: 'linear-gradient(90deg, transparent 0%, #ff4444 50%, transparent 100%)' }}
            />
            <h3 
              className="font-rajdhani font-bold uppercase mb-4"
              style={{ color: '#ff4444', fontSize: '22px', letterSpacing: '1px' }}
            >
              Why was this estimate lost?
            </h3>
            <div className="space-y-3 mb-6">
              {[
                { value: "price", label: "Price too high" },
                { value: "timeline", label: "Timeline didn't work" },
                { value: "competitor", label: "Client chose competitor" },
                { value: "delayed", label: "Client delayed/cancelled" },
                { value: "other", label: "Other" },
              ].map((option) => (
                <label 
                  key={option.value}
                  className="flex items-center gap-3 p-3 rounded-md cursor-pointer transition-all"
                  style={{
                    backgroundColor: markLostModal.reason === option.value ? 'rgba(255, 68, 68, 0.1)' : 'transparent',
                    border: `1px solid ${markLostModal.reason === option.value ? 'rgba(255, 68, 68, 0.4)' : 'rgba(148, 163, 184, 0.2)'}`,
                  }}
                >
                  <input
                    type="radio"
                    name="lostReason"
                    value={option.value}
                    checked={markLostModal.reason === option.value}
                    onChange={(e) => setMarkLostModal(prev => ({ ...prev, reason: e.target.value }))}
                    className="accent-red-500"
                  />
                  <span style={{ color: '#e2e8f0' }}>{option.label}</span>
                </label>
              ))}
            </div>
            <textarea
              placeholder="Optional notes..."
              value={markLostModal.notes}
              onChange={(e) => setMarkLostModal(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full h-20 p-3 rounded-md text-sm resize-none mb-6"
              style={{
                backgroundColor: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                color: '#e2e8f0',
              }}
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setMarkLostModal({ open: false, estimate: null, reason: "", notes: "" })}
                className="px-6 py-3 rounded-md font-bold text-sm transition-all"
                style={{
                  backgroundColor: 'rgba(148, 163, 184, 0.1)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  color: '#94a3b8',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMarkLost}
                className="px-6 py-3 rounded-md font-bold text-sm transition-all"
                style={{
                  background: 'linear-gradient(135deg, #ff4444 0%, #dc2626 100%)',
                  border: '1px solid #ff4444',
                  color: '#ffffff',
                  boxShadow: '0 0 20px rgba(255, 68, 68, 0.3)',
                }}
              >
                Mark as Lost
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Follow Up Modal */}
      {followUpModal.open && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-5"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(4px)', zIndex: 9999 }}
          onClick={() => setFollowUpModal({ open: false, estimate: null, note: "", nextDate: "" })}
        >
          <div 
            className="relative w-full max-w-md rounded-lg p-8"
            style={{
              backgroundColor: '#0f172a',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              borderLeft: '4px solid #3b82f6',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="absolute top-0 left-0 right-0 h-0.5"
              style={{ background: 'linear-gradient(90deg, transparent 0%, #3b82f6 50%, transparent 100%)' }}
            />
            <h3 
              className="font-rajdhani font-bold uppercase mb-4"
              style={{ color: '#3b82f6', fontSize: '22px', letterSpacing: '1px' }}
            >
              Log Follow-Up Activity
            </h3>
            <div className="p-4 rounded-md mb-6" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
              <p className="text-sm" style={{ color: '#94a3b8' }}>
                {followUpModal.estimate?.name}
              </p>
              <p className="text-sm font-bold mt-1" style={{ color: '#3b82f6' }}>
                {followUpModal.estimate?.clientName}
              </p>
              <p className="font-mono text-sm mt-1" style={{ color: '#10b981' }}>
                ${followUpModal.estimate?.amount?.toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-bold uppercase mb-2" style={{ color: '#64748b' }}>
                What did you discuss?
              </label>
              <textarea
                placeholder="Enter notes from your follow-up..."
                value={followUpModal.note}
                onChange={(e) => setFollowUpModal(prev => ({ ...prev, note: e.target.value }))}
                className="w-full h-24 p-3 rounded-md text-sm resize-none"
                style={{
                  backgroundColor: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  color: '#e2e8f0',
                }}
              />
            </div>
            <div className="mb-6">
              <label className="block text-xs font-bold uppercase mb-2" style={{ color: '#64748b' }}>
                Next follow-up date (optional)
              </label>
              <input
                type="date"
                value={followUpModal.nextDate}
                onChange={(e) => setFollowUpModal(prev => ({ ...prev, nextDate: e.target.value }))}
                className="w-full p-3 rounded-md text-sm"
                style={{
                  backgroundColor: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  color: '#e2e8f0',
                }}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setFollowUpModal({ open: false, estimate: null, note: "", nextDate: "" })}
                className="px-6 py-3 rounded-md font-bold text-sm transition-all"
                style={{
                  backgroundColor: 'rgba(148, 163, 184, 0.1)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  color: '#94a3b8',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleFollowUp}
                className="px-6 py-3 rounded-md font-bold text-sm transition-all"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: '1px solid #3b82f6',
                  color: '#ffffff',
                  boxShadow: '0 0 20px rgba(59, 130, 246, 0.3)',
                }}
              >
                Save Follow-Up
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Critical Pulse Animation */}
      <style>{`
        @keyframes criticalPulse {
          0%, 100% { text-shadow: 0 0 10px rgba(255, 68, 68, 0.3); }
          50% { text-shadow: 0 0 20px rgba(255, 68, 68, 0.6); }
        }
      `}</style>
      </div>
    </div>
  );
};

export default EstimatesPage;
